<template>
  <div
    class="field-input"
    :class="[`field-input--${field.type.toLowerCase()}`, { 'is-invalid': !!error }]"
    :title="error || field.label"
  >
    <!-- Checkbox: casilla centrada -->
    <input
      v-if="isCheckbox"
      type="checkbox"
      class="field-input__checkbox"
      :checked="!!value"
      @change="$emit('input', $event.target.checked)"
    />

    <!-- Texto multilínea -->
    <textarea
      v-else-if="isTextarea"
      class="field-input__control field-input__textarea"
      :style="controlStyle"
      :placeholder="field.placeholder || field.label"
      :value="value == null ? '' : value"
      @input="$emit('input', $event.target.value)"
    ></textarea>

    <!-- Lista desplegable -->
    <select
      v-else-if="isSelect"
      class="field-input__control"
      :style="controlStyle"
      :value="value == null ? '' : value"
      @change="$emit('input', $event.target.value)"
    >
      <option value="" disabled>
        {{ field.placeholder || field.label || 'Selecciona...' }}
      </option>
      <option
        v-for="(opt, i) in options"
        :key="i"
        :value="opt.value"
      >
        {{ opt.label || opt.value }}
      </option>
    </select>

    <!-- Resto de tipos: input de texto/fecha/numero -->
    <input
      v-else
      class="field-input__control"
      :type="htmlType"
      :style="controlStyle"
      :placeholder="field.placeholder || field.label"
      :value="value == null ? '' : value"
      @input="$emit('input', $event.target.value)"
      @blur="onBlur"
    />
  </div>
</template>

<script>

import { FIELD_TYPES } from '@/febos/global/pdf/formBuilder/model/fieldModel';
import { formatRut } from '@/febos/global/pdf/formBuilder/utils/rutUtils';

/**
 * Input de un campo en modo "completar". Rellena su contenedor (el tamaño y la
 * posición los fija el PdfFormViewer). No permite mover el campo.
 *
 * La validación (requerido + formato por tipo) la calcula el viewer con el
 * modelo; aquí solo se refleja vía la prop `error` (mensaje o null) para marcar
 * visualmente el campo. Para RUT, además se formatea al salir del foco.
 */
export default {
  name: 'FieldInput',
  props: {
    field: { type: Object, required: true },
    value: { type: null, default: null },
    // Tamaño de fuente configurado (px pantalla, ya escalado). Null = usar el
    // tamaño por defecto del input.
    fontSizePx: { type: Number, default: null },
    // Mensaje de error de validación (null si es válido). Lo provee el viewer.
    error: { type: String, default: null },
  },
  computed: {
    isCheckbox() {
      return this.field.type === FIELD_TYPES.CHECKBOX;
    },
    isSelect() {
      return this.field.type === FIELD_TYPES.SELECT;
    },
    isTextarea() {
      return this.field.type === FIELD_TYPES.TEXTAREA;
    },
    options() {
      return Array.isArray(this.field.options) ? this.field.options : [];
    },
    // Alineación y tamaño de fuente del input según el estilo del campo, para
    // que el modo completar se vea igual que el editor y el PDF estampado.
    controlStyle() {
      const alignment = (this.field.style && this.field.style.alignment) || 'left';
      const style = { textAlign: alignment };
      if (this.fontSizePx != null && this.fontSizePx > 0) {
        style.fontSize = `${this.fontSizePx}px`;
      }
      return style;
    },
    htmlType() {
      switch (this.field.type) {
        case FIELD_TYPES.DATE:
          return 'date';
        case FIELD_TYPES.NUMBER:
          return 'number';
        case FIELD_TYPES.EMAIL:
          return 'email';
        default:
          // TEXT, RUT y cualquier otro -> texto plano.
          return 'text';
      }
    },
  },
  methods: {
    onBlur() {
      // Para RUT con contenido, normalizar al salir SIN puntos (18331089-5):
      // el valor almacenado queda limpio; el formato con puntos es solo visual.
      if (this.field.type === FIELD_TYPES.RUT && this.value) {
        const normalized = formatRut(String(this.value), false);
        if (normalized && normalized !== this.value) {
          this.$emit('input', normalized);
        }
      }
    },
  },
};

</script>

<style lang="scss" scoped>

.field-input {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
}

.field-input__control {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid #c3c3d6;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.9);
  padding: 0 4px;
  font-size: 11px;
  color: #1f1f3d;
  outline: none;
}

.field-input__control:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.25);
}

.field-input__textarea {
  resize: none;
  padding: 2px 4px;
  line-height: 1.25;
  overflow: auto;
}

.field-input__checkbox {
  margin: 0 auto;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.field-input.is-invalid .field-input__control {
  border-color: #ea5455;
  background: rgba(234, 84, 85, 0.08);
}

.field-input.is-invalid .field-input__checkbox {
  outline: 2px solid #ea5455;
}

</style>
